import { ArrowRight, Report, Time } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { ReactNode, useState } from 'react';
import { cn } from 'tailwind-variants';

import { latestCampaignQueryOptions } from '@/api/latestCampaign';
import { listReportQueryOptions } from '@/api/listReport';
import { useIsAdmin } from '@/auth/isAdmin';
import { useAuthStore } from '@/auth/store';
import { Avatar } from '@/components/input/avatar';
import { Badge } from '@/components/input/badge';
import { Button, buttonVariants } from '@/components/input/button';
import { TodoCycle, useTodoItems } from '@/hooks/useTodoItems';
import { isFinishedStatus } from '@/util/evaluationTargets';
import { toReportLinks } from '@/util/reportLinks';
import { deadlineOf, formatMinutes, formatMonthDay, TodoItem, TodoSummary } from '@/util/todoItems';

export const Route = createFileRoute('/dashboard/_authenticated/')({
  component: RouteComponent,
  staticData: { pageWidth: 'wide' },
})

// Participant home (spec 012): the evaluations hub. Hero (deadline, progress, next up) → "To do"
// list → "Latest reports". UI contract: specs/012-evaluations-home-todo/contracts/ui.md
function RouteComponent() {
  const { name } = useAuthStore();
  const isAdmin = useIsAdmin();

  if (isAdmin) {
    return <Navigate to="/dashboard/admin" />;
  }

  return (
    <div className="grid gap-10">
      <div className="flex min-w-0 flex-col gap-10">
        <h1 className="text-page-title text-primary-700">Hey {name}!</h1>
        <TodoSection />
      </div>
      <LatestReports />
    </div>
  );
}

function TodoSection() {
  const todo = useTodoItems();

  if (todo.state === "loading") {
    return <TodoSkeleton />;
  }

  if (todo.state === "error") {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-error-300">Couldn't load your evaluations.</div>
        <Button onClick={todo.retry} size="sm" type="button">Retry</Button>
      </div>
    );
  }

  if (todo.state === "empty" || todo.campaigns.length === 0) {
    return (
      <div className="
        rounded-lg border-2 border-primary-100 p-8 text-body2 text-neutral-600
      ">
        Nothing to evaluate right now
      </div>
    );
  }

  // One hero + list per open campaign: each has its own deadline and progress.
  const several = todo.campaigns.length > 1;
  return (
    <>
      {todo.campaigns.map(({ cycle, items, summary }) => (
        <div className="flex flex-col gap-10" key={cycle.id}>
          <TodoHero cycle={cycle} summary={summary} />
          <section className="flex flex-col gap-4">
            <SectionHeading>{several ? `To do · ${cycle.name}` : "To do"}</SectionHeading>
            <ul className="
              flex flex-col overflow-hidden rounded-lg border-2
              border-primary-100
            ">
              {items.map((item) => (
                <TodoRow item={item} key={item.evaluationId} />
              ))}
            </ul>
          </section>
        </div>
      ))}
    </>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="
      border-b-2 border-b-primary-100 pb-3 text-h3 text-primary-700
    ">
      {children}
    </h2>
  );
}

function evaluationsLabel(count: number) {
  return `${count} ${count === 1 ? "evaluation" : "evaluations"}`;
}

function TodoHero({ cycle, summary }: { cycle: TodoCycle; summary: TodoSummary }) {
  const [now] = useState(() => new Date());
  const deadline = deadlineOf(cycle.endsAt, now);
  const { nextUp } = summary;

  return (
    <div className="
      flex flex-col gap-8 rounded-lg bg-primary-default p-8 text-neutral-white
      md:flex-row md:items-center md:justify-between
    ">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {deadline && (
          <div className={cn(
            `
              flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-body3
              font-semibold
            `,
            deadline.tone === "error" ? "bg-error-100 text-error-300" : `
              bg-warning-100 text-warning-300
            `,
          )}>
            <Time size="16" />
            {deadline.label}
          </div>
        )}
        <div className="text-h2">
          {summary.remaining > 0
            ? `You have ${evaluationsLabel(summary.remaining)} to finish`
            : "All your evaluations are complete"}
        </div>
        <div className="text-body2 text-primary-100">
          {cycle.name}
          {summary.minutesLeft > 0 && ` · ${formatMinutes(summary.minutesLeft)} left in total`}
        </div>
        <div className="flex items-center gap-4">
          <div
            aria-label="Evaluations done"
            aria-valuemax={summary.total}
            aria-valuemin={0}
            aria-valuenow={summary.done}
            className="
              h-2 max-w-md flex-1 overflow-hidden rounded-full bg-primary-500
            "
            role="progressbar"
          >
            <div
              className="h-full rounded-full bg-success-200"
              style={{ width: `${summary.total > 0 ? (summary.done / summary.total) * 100 : 0}%` }}
            />
          </div>
          <div className="shrink-0 text-body3 font-semibold">{summary.done} of {summary.total} done</div>
        </div>
      </div>
      {nextUp && (
        <div className="
          flex flex-col gap-2
          md:w-80
        ">
          <div className="text-body3 text-primary-100">Next up</div>
          <Link
            className="
              flex min-h-11 items-center justify-between gap-4 rounded-lg
              bg-neutral-white px-4 py-3 text-primary-700
              hover:bg-primary-50
            "
            {...nextUp.target}
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-body2 font-semibold">
                {nextUp.status === "inProgress" ? "Continue" : "Start"}{" "}
                {nextUp.kind === "self" ? "self-evaluation" : `evaluation of ${nextUp.evaluatee.name}`}
              </span>
              <span className="truncate text-body3 text-neutral-600">
                {[...nextUp.formNames, `${nextUp.total - nextUp.answered} questions left`].join(" · ")}
              </span>
            </div>
            <ArrowRight className="shrink-0" size="20" />
          </Link>
        </div>
      )}
    </div>
  );
}

function rowMeta(item: TodoItem) {
  if (isFinishedStatus(item.status)) {
    const saved = formatMonthDay(item.lastSavedAt);
    return [...item.formNames, saved ? `All answers saved · ${saved}` : "All answers saved"];
  }
  return item.minutesLeft > 0 ? [...item.formNames, `~${item.minutesLeft} min`] : item.formNames;
}

function TodoStatusBadge({ item }: { item: TodoItem }) {
  if (item.status === "inProgress") {
    return <Badge intent="primary">In progress · {item.answered}/{item.total}</Badge>;
  }
  if (item.status === "notStarted") {
    return <Badge intent="warning">Not started</Badge>;
  }
  if (item.status === "ready") {
    return <Badge intent="success">All answered</Badge>;
  }
  return <Badge intent="success">Done</Badge>;
}

// Stretched over the whole row (`after:inset-0`), so the row is one click target and one tab stop;
// the row shows the focus ring (`focus-within`). No press scale: a transform would shrink the overlay mid-click.
const ROW_LINK = "after:absolute after:inset-0 focus-visible:outline-none active:scale-none";

function TodoAction({ item }: { item: TodoItem }) {
  if (isFinishedStatus(item.status)) {
    return (
      <Link
        className={cn(`
          flex min-h-11 items-center px-4 text-body2 font-semibold
          text-primary-default
          hover:underline
        `, ROW_LINK)}
        {...item.target}
      >
        Review
      </Link>
    );
  }
  return (
    <Link
      className={buttonVariants({
        intent: item.status === "inProgress" ? "primary" : "default",
        size: "sm",
        className: cn(`
          min-h-11 justify-center font-semibold
          sm:w-28
        `, ROW_LINK),
      })}
      {...item.target}
    >
      {item.status === "inProgress" ? "Continue" : "Start"}
    </Link>
  );
}

function TodoRow({ item }: { item: TodoItem }) {
  const meta = rowMeta(item).join(" · ");
  return (
    <li className={cn(
      `
        relative flex flex-wrap items-center gap-x-4 gap-y-3 border-b
        border-b-primary-100 px-5 py-4 transition-colors
        last:border-b-0
        focus-within:ring-2 focus-within:ring-primary-500
        focus-within:ring-inset
        hover:bg-primary-100/60
      `,
      item.status === "inProgress" && "bg-primary-50",
      isFinishedStatus(item.status) && "bg-neutral-50",
    )}>
      <div className="flex min-w-0 flex-1 basis-64 items-center gap-4">
        <Avatar className="shrink-0" name={item.evaluatee.name} pictureBase64={item.evaluatee.picture} />
        <div className="flex min-w-0 flex-col">
          {/* Status next to the title (spec 019 US4), not at the far end of the row. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <div className={cn(
              "min-w-0 truncate text-body2 font-semibold",
              isFinishedStatus(item.status) ? "text-neutral-600" : `
                text-primary-700
              `,
            )}>
              {item.title}
            </div>
            <TodoStatusBadge item={item} />
          </div>
          {meta && (
            <div className="truncate text-body3 text-neutral-600" title={meta}>{meta}</div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center">
        <TodoAction item={item} />
      </div>
    </li>
  );
}

function TodoSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading your evaluations" className="
      flex flex-col gap-10
    ">
      <div className="h-44 animate-pulse rounded-lg bg-primary-100" />
      <div className="flex flex-col gap-2">
        {[0, 1, 2].map((key) => (
          <div className="h-18 animate-pulse rounded-lg bg-neutral-100" key={key} />
        ))}
      </div>
    </div>
  );
}

const LATEST_REPORTS_COUNT = 3;

function LatestReports() {
  const { userId } = useAuthStore();
  const { data: latestCampaign } = useQuery(latestCampaignQueryOptions(userId!));
  const { data: reports } = useQuery(listReportQueryOptions(userId!, latestCampaign?.latestCampaignId));
  const links = toReportLinks(reports?.campaigns).slice(0, LATEST_REPORTS_COUNT);

  if (links.length === 0) {
    return;
  }

  return (
    <section className="flex flex-col gap-4">
      <SectionHeading>Latest reports</SectionHeading>
      <div className="flex flex-wrap gap-4">
        {links.map((link) => (
          <Link
            className="
              flex min-h-11 items-center gap-3 rounded-lg border-2
              border-primary-100 px-5 py-3
              hover:bg-primary-50
            "
            key={link.campaignId}
            to={link.target}
          >
            <Report className="size-6 shrink-0 text-primary-default" />
            <div className="flex flex-col">
              <span className="text-body2 font-semibold text-primary-700">{link.label}</span>
              <span className="text-body3 text-neutral-600">{link.period}</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
